import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Avatar, Box, Chip, Tooltip, Typography } from '@mui/material';
import building from '../../../../../../public/assets/images/home-building.png';
import { Edit2, User } from 'iconsax-react';
import { yellow100, yellow300, yellowMain } from '../../../../constants/color';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { uploadProfilePictureAsync } from '@application/features/freelancer/freelancerThunks';
import { translate } from '@application/common/helpers/translate';
import { centered } from '../../account/style';
import { UserStatus } from '@application/common/enums/user-status';
import { getColorAndBackground } from '@ui/constants/helper';
/** Small profile-status badge shown next to the name (replaces the large status block). */
const profileStatusBadge = (status) => {
    switch (status) {
        case UserStatus.Approved:
            return { label: 'Profile approved', ...getColorAndBackground('Approved') };
        case UserStatus.Pending:
            return { label: 'Profile pending approval', ...getColorAndBackground('Pending') };
        case UserStatus.Disapproved:
            return { label: 'Profile rejected', ...getColorAndBackground('Rejected') };
        default:
            return undefined;
    }
};
export const UserProfileCard = ({ profilePicture, profileStatus }) => {
    const statusBadge = profileStatusBadge(profileStatus);
    const { freelancer } = useAppSelector((state) => state.freelancer);
    const dispatch = useAppDispatch();
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);
    const [avatarImage, setAvatarImage] = useState(null);
    const handleImageChange = (event) => {
        const file = event.target.files && event.target.files[0];
        if (file) {
            const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
            const maxSizeInMB = 1;
            const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
            if (!validTypes.includes(file.type)) {
                setError('Only PNG, JPEG, and JPG files are allowed.');
                return;
            }
            if (file.size > maxSizeInBytes) {
                setError(translate('validations.maxAllowedFileSize'));
                return;
            }
            setError(null);
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatarImage(reader.result);
            };
            reader.readAsDataURL(file);
            dispatch(uploadProfilePictureAsync(file));
        }
    };
    useEffect(() => {
        setAvatarImage(profilePicture);
    }, [profilePicture]);
    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const file = event.dataTransfer.files[0];
        if (file) {
            const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
            const maxSizeInMB = 1;
            const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
            if (!validTypes.includes(file.type)) {
                setError('Only PNG, JPEG, and JPG files are allowed.');
                return;
            }
            if (file.size > maxSizeInBytes) {
                setError(translate('validations.maxAllowedFileSize'));
                return;
            }
            setError(null);
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatarImage(reader.result);
            };
            reader.readAsDataURL(file);
            dispatch(uploadProfilePictureAsync(file));
        }
    };
    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };
    const handleEditClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };
    return (_jsxs(Box, { sx: {
            height: '365px',
            borderRadius: '25px',
            backgroundImage: `url(${building})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: '#fffff',
            opacity: 15,
            padding: '20px 20px',
            position: 'relative',
            cursor: 'pointer',
        }, onDrop: handleDrop, onDragOver: handleDragOver, children: [!avatarImage ? (_jsx("label", { htmlFor: 'avatarInput', children: _jsxs(Box, { sx: {
                        width: '135px',
                        height: '135px',
                        borderRadius: '50%',
                        border: '1px dashed #FFBC00',
                        background: yellow100,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative',
                        zIndex: 10
                    }, children: [_jsx(Box, { sx: {
                                zIndex: 1,
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)'
                            }, children: _jsx(User, { style: { width: '80px', height: '80px', color: yellow300 } }) }), _jsx(Box, { sx: {
                                zIndex: 9,
                                textAlign: 'center'
                            }, children: _jsxs(Typography, { variant: 'subtitle2', color: yellowMain, children: ["Upload your profile picture ", _jsx("span", { style: { textDecoration: 'underline', cursor: 'pointer' }, children: "browse" })] }) })] }) })) : (_jsxs(Box, { position: 'relative', children: [_jsx(Avatar, { src: avatarImage, sx: { width: '130px', height: '130px', marginBottom: '10px' } }), _jsx(Box, { sx: {
                            position: 'absolute',
                            bottom: 20,
                            right: 10,
                            width: 30,
                            height: 30,
                            background: 'white',
                            borderRadius: '50%',
                            ...centered,
                            cursor: 'pointer'
                        }, onClick: handleEditClick, children: _jsx(Edit2, { size: '18px', color: '#FFBC00' }) })] })), _jsx("input", { id: 'avatarInput', type: 'file', accept: 'image/*', onChange: handleImageChange, style: { display: 'none' }, ref: fileInputRef }), error && (_jsx(Typography, { color: 'error', textAlign: 'center', variant: 'caption', children: error })), _jsx(Typography, { fontSize: '24px', textAlign: 'center', fontWeight: 500, color: '#141414', mt: error ? 0 : 2, children: (freelancer?.firstName ?? '') + ' ' + (freelancer?.lastName ?? '') }), statusBadge && (_jsx(Box, { component: 'span', sx: {
                    mt: 0.5,
                    px: 1.25,
                    py: 0.25,
                    fontSize: '12px',
                    fontWeight: 500,
                    borderRadius: '20px',
                    color: statusBadge.color,
                    backgroundColor: statusBadge.background
                }, children: statusBadge.label })), freelancer.contractType && (_jsx(Chip, { size: 'small', label: `Contract type: ${freelancer.contractType}`, sx: { mt: 1, backgroundColor: '#FFFFFF', border: '1px solid #D7D7D7', borderRadius: '25px', color: '#141414' } })), _jsxs(Box, { sx: {
                    position: 'absolute',
                    bottom: '-20px',
                    left: 0,
                    height: '80px',
                    width: '100%',
                    background: '#D9D9D9',
                    borderRadius: '0px 0px 25px 25px',
                    opacity: 75,
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    alignItems: 'center',
                    borderTop: '1px solid #B1B1B1'
                }, children: [_jsx(Typography, { variant: 'subtitle2', color: '#555555', children: "Latest approved assignment" }), _jsx(Tooltip, { title: freelancer.currentAssignmentName || '', arrow: true, children: _jsx(Typography, { fontSize: freelancer.currentAssignmentName ? '24px' : '16px', fontWeight: freelancer.currentAssignmentName ? 550 : 400, color: freelancer.currentAssignmentName ? '#141414' : '#555555', sx: {
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                maxWidth: '80%'
                            }, children: freelancer.currentAssignmentName || 'None yet' }) })] })] }));
};
